import {
  buildWsUrl,
  computeBackoff,
  WS_CLOSE_INVALID_TICKET,
  WS_CLOSE_REAUTH,
} from '../utils/connection'
import { CHAT_ERROR_FORBIDDEN, CHAT_ERROR_SESSION_CLOSED, CHAT_ERROR_UNAVAILABLE } from './staffToken'

export const SOCKET_STATUS_IDLE = 'idle'
export const SOCKET_STATUS_CONNECTING = 'connecting'
export const SOCKET_STATUS_OPEN = 'open'
export const SOCKET_STATUS_RECONNECTING = 'reconnecting'
export const SOCKET_STATUS_STOPPED = 'stopped'

const DEFAULT_HEARTBEAT_SEC = 25
const TYPING_THROTTLE_MS = 3000

const FATAL_ERROR_CODES = [CHAT_ERROR_UNAVAILABLE, CHAT_ERROR_FORBIDDEN, CHAT_ERROR_SESSION_CLOSED]

/**
 * WebSocket nativo do chat (só entrega eventos; comandos são REST).
 *
 * - ticket de uso único via `getTicket()` a cada (re)conexão;
 * - `ping` a cada `heartbeatSec` do frame `hello`;
 * - reconexão com backoff exponencial + jitter (1s → 30s);
 * - close 4009 (reautenticar) → ticket novo e reconexão imediata;
 * - `onReconnected` é chamado a cada `hello` depois do primeiro (catch-up REST).
 */
export default class ChatSocket {
  constructor({
    getTicket, getConnectionInfo, onFrame, onStatus, onReconnected, onFatal,
  }) {
    this.getTicket = getTicket
    this.getConnectionInfo = getConnectionInfo
    this.onFrame = onFrame || (() => {})
    this.onStatus = onStatus || (() => {})
    this.onReconnected = onReconnected || (() => {})
    this.onFatal = onFatal || (() => {})

    this.ws = null
    this.stopped = true
    this.attempt = 0
    this.connectionId = 0
    this.hasConnectedBefore = false
    this.reconnectTimer = null
    this.heartbeatTimer = null
    this.lastTypingSentAt = {}
  }

  start() {
    if (!this.stopped) return
    this.stopped = false
    this.attempt = 0
    this.hasConnectedBefore = false
    this.open()
  }

  stop() {
    this.stopped = true
    this.connectionId += 1
    this.clearTimers()
    if (this.ws) {
      try {
        this.ws.close(1000, 'logout')
      } catch (e) {
        // socket já fechado
      }
    }
    this.ws = null
    this.setStatus(SOCKET_STATUS_STOPPED)
  }

  setStatus(status) {
    this.status = status
    this.onStatus(status)
  }

  clearTimers() {
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.reconnectTimer = null
    this.heartbeatTimer = null
  }

  async open() {
    if (this.stopped) return
    this.connectionId += 1
    const myConnection = this.connectionId
    this.setStatus(this.hasConnectedBefore ? SOCKET_STATUS_RECONNECTING : SOCKET_STATUS_CONNECTING)

    let url
    try {
      const ticket = await this.getTicket()
      const info = await this.getConnectionInfo()
      if (this.stopped || myConnection !== this.connectionId) return
      url = buildWsUrl({
        wsUrl: info.wsUrl,
        apiBaseUrl: info.apiBaseUrl,
        ticketPath: ticket.url,
        ticket: ticket.ticket,
      })
    } catch (error) {
      if (this.stopped || myConnection !== this.connectionId) return
      if (error && FATAL_ERROR_CODES.includes(error.code)) {
        this.stopped = true
        this.setStatus(SOCKET_STATUS_STOPPED)
        this.onFatal(error)
        return
      }
      this.scheduleReconnect()
      return
    }

    let ws
    try {
      ws = new WebSocket(url)
    } catch (error) {
      this.scheduleReconnect()
      return
    }
    this.ws = ws

    ws.onmessage = event => {
      if (myConnection !== this.connectionId) return
      this.handleMessage(event.data)
    }
    ws.onclose = event => {
      if (myConnection !== this.connectionId) return
      this.handleClose(event.code)
    }
    // onerror sempre é seguido de onclose; a reconexão é tratada lá.
    ws.onerror = () => {}
  }

  handleMessage(raw) {
    let frame
    try {
      frame = JSON.parse(raw)
    } catch (e) {
      return
    }
    if (!frame || typeof frame.type !== 'string') return

    if (frame.type === 'hello') {
      this.attempt = 0
      const heartbeatSec = Number(frame.data && frame.data.heartbeatSec) || DEFAULT_HEARTBEAT_SEC
      this.startHeartbeat(heartbeatSec)
      this.setStatus(SOCKET_STATUS_OPEN)
      if (this.hasConnectedBefore) this.onReconnected()
      this.hasConnectedBefore = true
      return
    }
    if (frame.type === 'pong') return
    this.onFrame(frame)
  }

  handleClose(code) {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = null
    this.ws = null
    if (this.stopped) return
    if (code === WS_CLOSE_REAUTH) {
      // Sessão do WS chegou ao limite ou foi revogada: ticket novo, sem esperar.
      this.attempt = 0
      this.open()
      return
    }
    if (code === WS_CLOSE_INVALID_TICKET) {
      // Ticket inválido/expirado: segue o backoff; o próximo open() pede outro ticket.
      this.scheduleReconnect()
      return
    }
    this.scheduleReconnect()
  }

  scheduleReconnect() {
    if (this.stopped) return
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    const delay = computeBackoff(this.attempt)
    this.attempt += 1
    this.setStatus(SOCKET_STATUS_RECONNECTING)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.open()
    }, delay)
  }

  startHeartbeat(heartbeatSec) {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer)
    this.heartbeatTimer = setInterval(() => this.send({ type: 'ping' }), heartbeatSec * 1000)
  }

  send(frame) {
    if (!this.ws || this.ws.readyState !== 1) return false
    try {
      this.ws.send(JSON.stringify(frame))
      return true
    } catch (e) {
      return false
    }
  }

  // Throttle de 3s por conversa, como pede o contrato.
  sendTyping(conversationId) {
    const now = Date.now()
    const last = this.lastTypingSentAt[conversationId] || 0
    if (now - last < TYPING_THROTTLE_MS) return
    if (this.send({ type: 'typing', data: { conversationId } })) {
      this.lastTypingSentAt[conversationId] = now
    }
  }
}
